/**
 * Project Task Kanban View
 *
 * Uses the global Kanban component with project-tasks-specific configuration.
 * All column definitions come from projectTaskStatusConfig.ts
 */

import React, { useState, useEffect, useCallback } from "react";
import { ProjectTask, ProjectTaskStatus } from "shared/types/renovation";
import { ProjectTaskCard } from "./ProjectTaskCard";
import { Kanban } from "shared/components/Kanban";
import { projectTasksKanbanConfig } from "shared/components/Kanban/configs/projectTasksKanbanConfig";
import { useSession } from "@clerk/clerk-react";
import { API_URL } from "constants/index";
import { fetchWithAuth } from "shared/api/fetch-with-auth";
import { toast } from "react-toastify";

interface ProjectTaskKanbanViewProps {
  tasks: ProjectTask[];
  projectId: number;
  onUpdate: () => void;
  onTaskClick?: (task: ProjectTask) => void;
  onAddClick?: (status: ProjectTaskStatus) => void;
}

export const ProjectTaskKanbanView = React.memo<ProjectTaskKanbanViewProps>(({
  tasks,
  onUpdate,
  onTaskClick,
  onAddClick,
}) => {
  const { session } = useSession();

  // Local state for optimistic updates
  const [localTasks, setLocalTasks] = useState(tasks);

  // Update local state when prop changes
  useEffect(() => {
    setLocalTasks(tasks);
  }, [tasks]);

  /**
   * Handle drop from the Kanban component
   */
  const handleDrop = async (
    taskId: string | number,
    newStatus: ProjectTaskStatus,
    oldStatus: ProjectTaskStatus,
  ) => {
    // OPTIMISTIC UPDATE: Update local state immediately
    setLocalTasks((prev) =>
      prev.map((t) => (t.id === taskId ? { ...t, status: newStatus } : t)),
    );

    // Make API call in background
    try {
      const response = await fetchWithAuth(
        `${API_URL}/renovation/project/task/${taskId}/status`,
        {
          method: "PUT",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({ status: newStatus }),
        },
        session,
      );

      if (!response.ok) {
        throw new Error("Failed to update task status");
      }

      toast.success(`Taak verplaatst naar "${newStatus}"`);
    } catch (error: any) {
      console.error("Error updating task status:", error);
      toast.error(
        `Fout bij verplaatsen taak: ${error.message || "Onbekende fout"}`,
      );

      // ROLLBACK: Revert local state on error
      setLocalTasks((prev) =>
        prev.map((t) => (t.id === taskId ? { ...t, status: oldStatus } : t)),
      );

      // Refetch to ensure data consistency
      onUpdate();
    }
  };

  /**
   * Render a project task card - MEMOIZED to prevent re-creating function on every render
   */
  const renderTaskCard = useCallback((task: ProjectTask) => (
    <ProjectTaskCard
      task={task}
      onClick={() => onTaskClick?.(task)}
      onBlockClick={(task) => onTaskClick?.(task)}
    />
  ), [onTaskClick]);

  return (
    <Kanban
      config={projectTasksKanbanConfig}
      items={localTasks}
      renderCard={renderTaskCard}
      onDrop={handleDrop}
      onAddClick={onAddClick}
      onCardClick={onTaskClick}
      dragActivationDistance={8} // Default 8px - optimal for drag responsiveness
    />
  );
});

ProjectTaskKanbanView.displayName = "ProjectTaskKanbanView";
